import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search, ArrowUp, ArrowDown, ArrowUpDown, RefreshCw, ShieldAlert,
  GitCompare, Info, ChevronRight, ChevronDown, AlertTriangle, Download, Database
} from 'lucide-react';
import { useToast } from './ToastContainer';
import api from '../utils/api';
import './NpsLedger.css';

const npsClass = (n) => (n > 0 ? 'pos' : n < 0 ? 'neg' : 'zero');
const fmtNps = (n) => (n === null || n === undefined ? '—' : `${n > 0 ? '+' : ''}${n}`);

// Riyadh (UTC+3) calendar date today — the default anchor for relative periods.
const riyadhToday = () => new Date(Date.now() + 3 * 3600 * 1000).toISOString().slice(0, 10);

const PERIODS = [
  ['all', 'All time'], ['day', 'Today'], ['week', 'This week'],
  ['month', 'This month'], ['ytd', 'Year to date'], ['custom', 'Custom'],
];
const SOURCES = [
  ['sheet', 'Google Sheet (live)'], ['push', 'Pushed (new API)'], ['all', 'Both'],
];

// One of the three column tallies rendered as a compact stat block.
const Tally = ({ label, tally, tone, foot }) => (
  <div className={`nl-ocard ${tone || ''}`}>
    <div className="nl-ocard-label">{label}</div>
    <div className={`nl-ocard-nps ${tally ? npsClass(tally.nps) : ''}`}>{tally ? tally.nps : '—'}</div>
    {tally && (
      <div className="nl-ocard-sub">
        <span>{tally.total.toLocaleString()} responses</span>
        <span className="nl-dot pos">{tally.promoters.toLocaleString()} promoters</span>
        <span className="nl-dot neu">{tally.neutrals.toLocaleString()} neutral</span>
        <span className="nl-dot neg">{tally.detractors.toLocaleString()} detractors</span>
      </div>
    )}
    {foot && <div className="nl-ocard-foot">{foot}</div>}
  </div>
);

const NpsLedger = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [typeFilter, setTypeFilter] = useState(''); // '' | 'Sales' | 'Delivery'
  const [source, setSource] = useState('sheet');     // 'sheet' | 'push' | 'all'
  const [grain, setGrain] = useState('all');          // period preset
  const [anchor] = useState(riyadhToday());           // anchor for relative periods
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('npsDelta');
  const [sortDir, setSortDir] = useState('desc');
  const [onlyChanged, setOnlyChanged] = useState(true);
  const [expanded, setExpanded] = useState(null); // `${type}\u0001${emp}`
  const { showError } = useToast();

  // Build the period half of the query from the current controls.
  const periodParams = useCallback(() => {
    if (grain === 'all') return {};
    if (grain === 'custom') {
      const p = { grain: 'custom' };
      if (customFrom) p.from = customFrom;
      if (customTo) p.to = customTo;
      return p;
    }
    return { grain, anchor };
  }, [grain, anchor, customFrom, customTo]);

  const queryParams = useCallback(() => ({
    ...(typeFilter ? { type: typeFilter } : {}),
    source,
    ...periodParams(),
  }), [typeFilter, source, periodParams]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/nps/ledger/compare', { params: queryParams() });
      if (res.data.success) setData(res.data);
      else showError('Failed to load the comparison');
    } catch (e) {
      console.error('compare load failed', e);
      showError('Failed to load the NPS Ledger comparison');
    } finally {
      setLoading(false);
    }
  }, [queryParams, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const doExport = async (view) => {
    try {
      setExporting(true);
      const params = { ...queryParams(), ...(view === 'trend' ? { view: 'trend', granularity: 'month' } : {}) };
      const resp = await api.get('/api/nps/ledger/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = resp.headers['content-disposition'] || '';
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : `nps-export${view === 'trend' ? '-trend' : ''}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('export failed', e);
      showError('Export failed — check the date range and try again');
    } finally {
      setExporting(false);
    }
  };

  const casesByEmp = useMemo(() => {
    const m = new Map();
    for (const c of data?.sensitiveCases || []) {
      for (const mem of c.members) {
        const k = `${c.type}\u0001${mem.employee_id}`;
        if (!m.has(k)) m.set(k, []);
        if (!m.get(k).some((x) => x.order_key === c.order_key)) m.get(k).push(c);
      }
    }
    return m;
  }, [data]);

  const rows = useMemo(() => {
    let r = data?.employees || [];
    if (onlyChanged) r = r.filter((e) => e.npsDelta !== 0 || e.replaced > 0);
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((e) => (e.name || '').toLowerCase().includes(q) || (e.employee_id || '').toLowerCase().includes(q));
    const dir = sortDir === 'asc' ? 1 : -1;
    const val = (e) => {
      switch (sortField) {
        case 'name': return (e.name || '').toLowerCase();
        case 'today': return e.salesAppToday ? e.salesAppToday.nps : -9999;
        case 'same': return e.newSameRules.nps;
        case 'corrected': return e.newCorrected.nps;
        case 'replaced': return e.replaced;
        case 'responses': return e.newSameRules.total;
        default: return e.npsDelta;
      }
    };
    return [...r].sort((a, b) => {
      const va = val(a), vb = val(b);
      if (typeof va === 'string') return va.localeCompare(vb) * dir;
      return (va - vb) * dir;
    });
  }, [data, onlyChanged, search, sortField, sortDir]);

  const toggleSort = (f) => {
    if (sortField === f) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(f); setSortDir('desc'); }
  };
  const SortIcon = ({ f }) => (sortField !== f
    ? <ArrowUpDown size={13} className="nl-sort" />
    : sortDir === 'asc' ? <ArrowUp size={13} className="nl-sort on" /> : <ArrowDown size={13} className="nl-sort on" />);

  const overall = data?.overall;
  const sum = data?.summary;
  const bounded = data?.bounded;
  const legacyApplicable = data?.legacyApplicable;
  const drift = sum ? sum.parity_mismatches : 0;
  const cachedAt = (!bounded && data?.cached && data?.built_at) ? data.built_at : null;
  const fmtAgo = (iso) => {
    const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return 'just now';
    if (s < 3600) return `${Math.round(s / 60)}m ago`;
    if (s < 86400) return `${Math.round(s / 3600)}h ago`;
    return `${Math.round(s / 86400)}d ago`;
  };

  return (
    <div className="nl-wrap">
      <div className="nl-head">
        <div>
          <h1 className="nl-title">NPS Ledger <span className="nl-tag">shadow</span></h1>
          <p className="nl-sub">The new scoring system, running in parallel. Nothing here is live yet — your current NPS page is untouched. This compares today's numbers against the new system so you can see exactly what the corrections would change before switching.</p>
        </div>
        <div className="nl-head-actions">
          {cachedAt && (
            <span className="nl-cachehint" title={`Served from the precomputed cache (built ${new Date(cachedAt).toLocaleString()}). New answers refresh it automatically.`}>
              <Database size={13} /> cached · {fmtAgo(cachedAt)}
            </span>
          )}
          <button className="nl-btn ghost" onClick={() => doExport('employees')} disabled={loading || exporting} title="Download this view as CSV">
            <Download size={16} className={exporting ? 'spin' : ''} /> Export CSV
          </button>
          <button className="nl-btn ghost" onClick={() => doExport('trend')} disabled={loading || exporting} title="Download NPS per month for the selected range">
            <Download size={16} /> Monthly trend
          </button>
          <button className="nl-btn" onClick={() => fetchData()} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      <div className="nl-controls">
        <div className="nl-filters">
          {[['', 'All'], ['Sales', 'Sales'], ['Delivery', 'Delivery']].map(([v, l]) => (
            <button key={v} className={`nl-chip ${typeFilter === v ? 'on' : ''}`} onClick={() => setTypeFilter(v)}>{l}</button>
          ))}
        </div>

        <div className="nl-seg" role="group" aria-label="Period">
          {PERIODS.map(([v, l]) => (
            <button key={v} className={grain === v ? 'on' : ''} onClick={() => setGrain(v)}>{l}</button>
          ))}
        </div>

        {grain === 'custom' && (
          <div className="nl-daterange">
            <label>From <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} /></label>
            <label>To <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} /></label>
          </div>
        )}

        <label className="nl-srcpick" title="Google Sheet = the current live feed. Pushed = responses sent through the new ingest API (for go-live testing).">
          <Database size={14} />
          <select value={source} onChange={(e) => setSource(e.target.value)}>
            {SOURCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>

      {loading && <div className="nl-loading"><RefreshCw size={22} className="spin" /> Crunching the ledger…</div>}

      {!loading && data && (
        <>
          <div className="nl-explain">
            <GitCompare size={18} />
            <div>
              <b>Three views of the same people.</b> <span className="nl-k today">Sales app today</span> is what your live system shows now.
              <span className="nl-k same"> New · same rules</span> is the new system counting <i>every</i> answer exactly like today — it should match.
              <span className="nl-k corr"> New · corrected</span> applies <b>Rule 1 (latest answer wins)</b> in shadow: when an order was answered more than once, only the newest counts.
              {data.period && <span className="nl-period"> Showing: <b>{data.period.label}</b>{data.source ? ` · source: ${data.source}` : ''}.</span>}
            </div>
          </div>

          <div className="nl-overall">
            {legacyApplicable ? (
              <Tally label="Sales app today" tally={overall.salesAppToday} tone="today" />
            ) : (
              <div className="nl-ocard note">
                <div className="nl-ocard-label">Sales app today</div>
                <div className="nl-ocard-na">N/A here</div>
                <div className="nl-ocard-sub">{bounded
                  ? <>The live app score is an all-time total with no per-day history, so it can't be sliced by period. Switch to <b>All time</b> to compare against it.</>
                  : <>The live app total reflects the <b>Google Sheet</b> feed, so it isn't comparable to pushed data. Switch source to <b>Google Sheet</b> to compare against it.</>}
                </div>
              </div>
            )}
            <Tally label="New · same rules" tally={overall.newSameRules} tone="same"
              foot={legacyApplicable ? <span className={`nl-parity ${drift ? 'warn' : 'ok'}`}>{drift ? `${drift} staff drifted since import` : 'matches live exactly'}</span> : null} />
            <Tally label="New · corrected" tally={overall.newCorrected} tone="corr"
              foot={<span className="nl-effect">{sum.answers_replaced.toLocaleString()} answers replaced · {sum.by_class.different_employee} between different staff</span>} />
          </div>

          {legacyApplicable && drift > 0 && (
            <div className="nl-drift">
              <Info size={15} />
              Parity was <b>exact</b> at import. Since then the nightly sync added newer answers, so {drift} employees now differ between “today” and the ledger snapshot. Re-running the import re-syncs them — this is drift, not an error.
            </div>
          )}

          <div className="nl-toolbar">
            <div className="nl-search">
              <Search size={16} />
              <input placeholder="Search employee or code…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <label className="nl-toggle">
              <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} />
              Only employees whose score changes
            </label>
            <span className="nl-count">{rows.length} shown · {sum.employees_changed} change · {sum.by_class.different_employee} sensitive</span>
          </div>

          <div className="nl-tablewrap">
            <table className="nl-table">
              <thead>
                <tr>
                  <th onClick={() => toggleSort('name')}>Employee <SortIcon f="name" /></th>
                  <th onClick={() => toggleSort('today')}>Today <SortIcon f="today" /></th>
                  <th onClick={() => toggleSort('same')}>New · same <SortIcon f="same" /></th>
                  <th onClick={() => toggleSort('corrected')}>New · corrected <SortIcon f="corrected" /></th>
                  <th onClick={() => toggleSort('npsDelta')}>Δ corrected <SortIcon f="npsDelta" /></th>
                  <th onClick={() => toggleSort('responses')}>Responses <SortIcon f="responses" /></th>
                  <th onClick={() => toggleSort('replaced')}>Replaced <SortIcon f="replaced" /></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => {
                  const k = `${e.type}\u0001${e.employee_id}`;
                  const cases = casesByEmp.get(k) || [];
                  const isOpen = expanded === k;
                  return (
                    <React.Fragment key={k}>
                      <tr className={`nl-row ${cases.length ? 'clickable' : ''}`} onClick={() => cases.length && setExpanded(isOpen ? null : k)}>
                        <td className="nl-emp">
                          {cases.length ? (isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="nl-spacer" />}
                          <span className="nl-name">{e.name}</span>
                          <span className="nl-type">{e.type}</span>
                          {e.sensitive && <span className="nl-badge sens" title="A newer answer on one of this person's orders belongs to a different employee"><ShieldAlert size={12} /> sensitive</span>}
                          {e.parityMatch === false && <span className="nl-badge drift" title="Differs from the live system due to answers added since import"><AlertTriangle size={12} /> drift</span>}
                        </td>
                        <td className={`nl-n ${e.salesAppToday ? npsClass(e.salesAppToday.nps) : ''}`}>{e.salesAppToday ? e.salesAppToday.nps : '—'}</td>
                        <td className={`nl-n ${npsClass(e.newSameRules.nps)}`}>{e.newSameRules.nps}</td>
                        <td className={`nl-n ${npsClass(e.newCorrected.nps)}`}>{e.newCorrected.nps}</td>
                        <td className={`nl-n delta ${npsClass(e.npsDelta)}`}>{fmtNps(e.npsDelta)}</td>
                        <td className="nl-muted">{e.newSameRules.total}{e.newCorrected.total !== e.newSameRules.total && <span className="nl-arrow"> → {e.newCorrected.total}</span>}</td>
                        <td className="nl-muted">{e.replaced || ''}</td>
                      </tr>
                      {isOpen && cases.map((c) => (
                        <tr key={k + c.order_key} className="nl-case">
                          <td colSpan={7}>
                            <div className="nl-caserow">
                              <span className="nl-order">{c.order_key} · {c.type}</span>
                              <div className="nl-members">
                                {c.members.map((m, i) => (
                                  <span key={i} className={`nl-mem ${m.status}`}>
                                    {m.local_date} · rating {m.rating} · {m.name}
                                    <b>{m.status === 'counted' ? ' ✓ counts' : ' replaced'}</b>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={7} className="nl-empty">No employees match.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default NpsLedger;
